import React, { FC } from 'react';
import parser from 'bbcode-to-react';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import PriceSuffix from '@components/PriceSuffix';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { batch, useDispatch, useSelector } from 'react-redux';
import useTranslations from '@hooks/useTranslations';
import { useUIDuck } from '@hooks/useUIDuck';
import { getMonthlyPriceObjByType, getTotalPriceObjByType } from '@utils/Price.utils';
import { IPriceV2Types } from '../../../services';
import Spinner from '@components/Spinner';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { findDefaultConfiguration, sortOffersDescByPriority } from '@utils/Offers.utils';
import { ITrimItem } from 'src/redux/trimSelector/trimSelector.duck.interface';
import { PaymentJourneyTypes } from 'src/partExchange/interfaces/Default';
import { TrimSelectProps } from '@components/v2/TrimSelect/index';
import { getTrimContent } from '../../../services/carList/utils/Car.utils';
import { ICarDetail } from '../../../redux/carDetails/carDetails.duck.interface';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';
import { ModalVersion } from '../../../constants/main';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import FilterDuck from 'src/redux/filters/filter.duck';
import { Redux } from 'src/redux/redux.interface';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import Link from '@components/Link';

export const TrimSelectTemplate: FC<TrimSelectProps> = ({ className, onTrimSelectClick, onTrimFeaturesClick }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { formatMoney } = useMoneyFormatter();
    const { carDetails } = useCarDetailsDuck();
    const { isLoading } = useUIDuck();
    const filters = useSelector((state) => FilterDuck.getFilters(state));
    const usingFilters = filters.filter((filter) => filter.value).length > 0;
    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const isBenefitsEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BENEFITS_MODE_ENABLED);
    const filteredTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredTrims(state));
    const isTrimCustomized = useSelector((state: Redux) => TrimSelectorDuck.isTrimCustomized(state));
    const trimItems: ITrimItem[] = filteredTrims.sort(sortOffersDescByPriority).map((item, index) => ({
        ...item,
        index,
    }));

    const openFeaturesModal = (carDetails: ICarDetail) => {
        dispatch(
            UIDuck.openModal({
                data: { carDetails },
                callbacks: {},
                modalType: UIDuck.MODAL_TYPES.FEATURES,
                modalVersion: ModalVersion.v2,
            })
        );
    };

    return (
        <div className={className}>
            {isLoading && <Spinner size={36} border={2} color="black" />}
            <div id="skip-to-content" className={`trims ${isLoading ? 'isLoading' : ''}`}>
                {trimItems.map((trim, index: number) => {
                    const trimContent = getTrimContent(trim);

                    const carDetailsId = carDetails.id || carDetails._id || carDetails.externalId;
                    // if currently rendered trim item is same as global car details then use details from it
                    // Features modal can change it
                    const isSelected = trim._id?.split('+')[0] === carDetailsId?.split('+')[0];
                    if (isSelected) {
                        trim = carDetails;
                    }

                    const defaultConfiguration =
                        isBenefitsEnabled &&
                        !usingFilters &&
                        !isTrimCustomized &&
                        findDefaultConfiguration(trim.offers, PaymentJourneyTypes.CASH);

                    const pricesArray = (defaultConfiguration || trim).prices;
                    const pricesV2Array = defaultConfiguration
                        ? defaultConfiguration.pricesV2
                        : trim.extraFields?.pricesV2 ?? trim.pricesV2;
                    const monthlyPrice = getMonthlyPriceObjByType(pricesArray, 'Employee');
                    const amountRounding = getMonthlyPriceObjByType(
                        trim?.prices,
                        StockFormatedPriceType.EMPLOYEE
                    )?.amountRounding;

                    return (
                        <div key={trim._id}>
                            <button
                                onClick={() => {
                                    if (!isSelected) {
                                        batch(() => {
                                            dispatch(CarDetailsDuck.setCurrentCarDetails(trim));
                                            dispatch(TrimSelectorDuck.setIsTrimCustomized(false));
                                        });
                                    }
                                    onTrimSelectClick(trim.specPack.title);
                                }}
                                className={`trim ${isSelected ? 'isSelected' : ''} ${
                                    trim.isDisabled ? 'isDisabled' : ''
                                }`}
                            >
                                <div className="inner">
                                    <span className="trimTitle" data-testid={`TESTING_TRIM_ITEM_SPECK_PACK_${index}`}>
                                        <span className={`icon ${isSelected ? 'selectedBorder' : ''}`}>
                                            {isSelected && <span className="selected" />}
                                        </span>
                                        {trim.specPack.title}
                                    </span>
                                    {trimContent.title && (
                                        <div
                                            className="trimContent title"
                                            data-testid={`TESTING_TRIM_ITEM_TITLE_${index}`}
                                        >
                                            {parser.toReact(trimContent.title)}
                                        </div>
                                    )}
                                    {trimContent.description && (
                                        <span className="trimDescription">
                                            <div
                                                className="trimContent description"
                                                data-testid={`TESTING_TRIM_ITEM_DESCRIPTION_${index}`}
                                            >
                                                {parser.toReact(trimContent.description)}
                                            </div>
                                        </span>
                                    )}
                                    <span className="trimPrice" data-testid={`TESTING_TRIM_ITEM_PRICE_${index}`}>
                                        {t('nameplate.priceFrom')}{' '}
                                        {formatMoney(
                                            isSOLEnabled
                                                ? getTotalPriceObjByType(pricesV2Array, IPriceV2Types.TOTAL_B2C_CASH)
                                                      ?.breakdown.finalCustomerCashPriceOnTheRoad
                                                : getTotalPriceObjByType(pricesV2Array, IPriceV2Types.TOTAL_B2C_CASH)
                                                      ?.finalPriceInclTax,
                                            true
                                        )}{' '}
                                        {monthlyPrice && (
                                            <span>
                                                <span className="or">{t('configurator.optionPrice.suffix')}</span>{' '}
                                                {formatMoney(monthlyPrice.amount, false, '', amountRounding)}{' '}
                                                <PriceSuffix isMonthlyPrice hideAsterisk />
                                            </span>
                                        )}
                                    </span>
                                    <Link
                                        label={t('modal.features.feature.link')}
                                        onClick={() => {
                                            openFeaturesModal(trim);
                                            onTrimFeaturesClick(t('modal.features.feature.link'));
                                        }}
                                        secondary
                                        dataTestId={`TESTING_TRIM_ITEM_FEATURES_OPEN_LINK_${index}`}
                                    />
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
